/**
 * Execution block: plays an external animation GLB on a SceneObject.
 * Trace extension — not part of KHR_interactivity spec.
 *
 * Config: target (path), src (GLB path), clip (clip name within GLB)
 * Data inputs: speed (float), loop (bool)
 * Flow: in → out (immediate), started (clip loaded and driving the object),
 *       done (on completion for non-looping)
 *
 * `out` fires before the async GLB load settles — sequencing "make visible
 * after the clip is driving" (the show-with-animation flash fix) must chain
 * from `started`, not `out`.
 */

import { FlowGraphAsyncExecutionBlock } from "@babylonjs/core/FlowGraph/flowGraphAsyncExecutionBlock";
import type { FlowGraphContext } from "@babylonjs/core/FlowGraph/flowGraphContext";
import type { FlowGraphSignalConnection } from "@babylonjs/core/FlowGraph/flowGraphSignalConnection";
import type { IFlowGraphBlockConfiguration } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import { RichTypeNumber, RichTypeBoolean } from "@babylonjs/core/FlowGraph/flowGraphRichTypes";
import { Animation } from '../../animation';
import { Logger } from '../../../utils/Logger';
import { resolveTarget } from './traceContext';

export class TracePlayExternalBlock extends FlowGraphAsyncExecutionBlock {
    private _targetPath: string;
    private _src: string;
    private _clip: string | undefined;
    private _generation = 0;
    readonly started: FlowGraphSignalConnection;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string; src?: string; clip?: string }) {
        super(config);
        this._targetPath = config?.target ?? '';
        this._src = config?.src ?? '';
        this._clip = config?.clip;
        this.registerDataInput("speed", RichTypeNumber, 1);
        this.registerDataInput("loop", RichTypeBoolean, false);
        this.started = this._registerSignalOutput("started");
    }

    public override _execute(context: FlowGraphContext, _callingSignal: FlowGraphSignalConnection): void {
        const target = resolveTarget(context, this._targetPath);
        if (!target) {
            this.out._activateSignal(context);
            return;
        }

        const animBehavior = target.getBehavior(Animation);
        if (!animBehavior) {
            Logger.warn(`[TracePlayExternalBlock] No Animation behavior on ${target.name}`);
            this.out._activateSignal(context);
            return;
        }

        if (!this._src) {
            Logger.warn(`[TracePlayExternalBlock] No src specified`);
            this.out._activateSignal(context);
            return;
        }

        const speed = this.getDataInput("speed")!.getValue(context) as number;
        const loop = this.getDataInput("loop")!.getValue(context) as boolean;
        const gen = ++this._generation;

        const playPromise = animBehavior.playExternal(this._src, {
            clip: this._clip,
            speed,
            loop,
            onStarted: () => {
                if (this._generation === gen) {
                    this.started._activateSignal(context);
                }
            },
        });

        this.out._activateSignal(context);

        // A rejected load (bad src, missing clip, network) must not surface as
        // an unhandled rejection — warn and swallow; `done` never fires.
        playPromise.then(() => {
            if (!loop && this._generation === gen) {
                this.done._activateSignal(context);
            }
        }).catch((err: unknown) => {
            Logger.warn(`[TracePlayExternalBlock] playExternal("${this._src}") failed: ${String(err)}`);
        });
    }

    public override _preparePendingTasks(_context: FlowGraphContext): void { /* no-op */ }

    public override _cancelPendingTasks(_context: FlowGraphContext): void {
        this._generation++;
    }

    public override getClassName(): string {
        return "TracePlayExternalBlock";
    }
}
